import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type Interview, type InterviewInput, type InviteContext } from "../lib/api";
import { todayInLondon } from "../lib/dates";
import { buildIcs, downloadIcs, googleCalendarUrl, inviteEmail, londonToUtc, outlookCalendarUrl, slotText, utcToLondon, type Audience } from "../lib/ics";

const FORMATS: { id: Interview["format"]; label: string }[] = [
  { id: "video", label: "Video" },
  { id: "phone", label: "Phone" },
  { id: "in_person", label: "In person" },
];
const DURATIONS = [30, 45, 60, 90, 120];
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/**
 * Interviews for one candidate on one job. KuduRMS doesn't send email yet, so each interview
 * offers calendar invites to download and attach (one for the candidate, one for the client),
 * a drafted email in the recruiter's own mail app, and links to add it to their own calendar.
 */
export function Interviews({ applicationId, canBook, onChanged }: { applicationId: string; canBook: boolean; onChanged: () => void }) {
  const [interviews, setInterviews] = useState<Interview[] | null>(null);
  const [invite, setInvite] = useState<InviteContext | null>(null);
  const [form, setForm] = useState<{ editing: Interview | null } | null>(null);
  const load = useAction();

  const reload = useCallback(
    () =>
      load.run(async () => {
        const r = await api.interviews(applicationId);
        setInterviews(r.interviews);
        setInvite(r.invite);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applicationId],
  );
  useEffect(() => void reload(), [reload]);

  const changed = async () => {
    setForm(null);
    await reload();
    onChanged();
  };

  const now = Date.now();
  const upcoming = (interviews ?? []).filter((i) => i.status === "scheduled" && new Date(i.startsAt).getTime() + i.durationMinutes * 60_000 > now);
  const past = (interviews ?? []).filter((i) => !upcoming.includes(i));

  return (
    <section className="section" aria-labelledby="interviews-h">
      <div className="section-head">
        <h2 id="interviews-h" className="label">
          Interviews
        </h2>
        {canBook && !form && (
          <button type="button" className="link-btn" onClick={() => setForm({ editing: null })}>
            + Book an interview
          </button>
        )}
      </div>
      <ErrorNotice error={load.error} />
      {form && <InterviewForm applicationId={applicationId} editing={form.editing} onCancel={() => setForm(null)} onSaved={() => void changed()} />}
      {interviews && interviews.length === 0 && !form && <p className="hint">No interviews booked yet.</p>}
      {interviews && interviews.length > 0 && (
        <p className="hint">
          See every interview in your own calendar automatically: <Link to="/calendar">subscribe to your calendar address</Link>.
        </p>
      )}
      {invite &&
        upcoming.map((i) => <InterviewCard key={i.id} interview={i} invite={invite} onEdit={() => setForm({ editing: i })} onChanged={() => void changed()} />)}
      {invite && past.length > 0 && (
        <details className="earlier">
          <summary className="label">Earlier or cancelled · {past.length}</summary>
          {past.map((i) => (
            <InterviewCard key={i.id} interview={i} invite={invite} onChanged={() => void changed()} />
          ))}
        </details>
      )}
    </section>
  );
}

function InterviewCard({ interview: i, invite, onEdit, onChanged }: { interview: Interview; invite: InviteContext; onEdit?: () => void; onChanged: () => void }) {
  const act = useAction();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const cancelled = i.status === "cancelled";
  const isPast = !cancelled && new Date(i.startsAt).getTime() < Date.now();
  const save = (audience: Audience) =>
    downloadIcs(`${cancelled ? "cancelled-" : ""}interview-${slug(invite.candidate.fullName)}-${audience === "candidate" ? "candidate" : slug(invite.company.name)}.ics`, buildIcs(i, invite, audience));
  const fmt = FORMATS.find((f) => f.id === i.format)!.label;

  return (
    <div className={`interview ${cancelled ? "cancelled" : ""}`}>
      <p className="interview-when">
        {cancelled && <span className="answer no">Cancelled</span>} <strong>{slotText(i.startsAt)}</strong> · {i.durationMinutes} min · {fmt}
      </p>
      <p className="secondary">
        {i.stageName}
        {i.location && <> · {i.location}</>}
        {i.interviewers && <> · with {i.interviewers}</>}
      </p>
      {i.notes && <p className="prewrap small-text">{i.notes}</p>}
      {!isPast && (
        <>
          <div className="invite-actions">
            <span className="label">{cancelled ? "Send the cancellation" : "Send the invite"}</span>
            <InviteFor label={invite.candidate.fullName} email={invite.candidate.email} onDownload={() => save("candidate")} mail={inviteEmail(i, invite, "candidate")} cancelled={cancelled} />
            {invite.contact ? (
              <InviteFor label={`${invite.contact.fullName} (client)`} email={invite.contact.email} onDownload={() => save("client")} mail={inviteEmail(i, invite, "client")} cancelled={cancelled} />
            ) : (
              <p className="hint">The job has no hiring contact, so there's no client invite. Set one on the job.</p>
            )}
            <p className="hint">
              Download the {cancelled ? "cancellation" : "invite"}, then attach it to the email that opens. It works in Outlook, Gmail and Apple Calendar.
            </p>
          </div>
          {!cancelled && (
            <div className="button-row">
              <a className="link-btn" href={googleCalendarUrl(i, invite)} target="_blank" rel="noopener">
                Add to my Google Calendar
              </a>
              <a className="link-btn" href={outlookCalendarUrl(i, invite)} target="_blank" rel="noopener">
                Add to my Outlook
              </a>
              {onEdit && (
                <button type="button" className="link-btn" onClick={onEdit}>
                  Change
                </button>
              )}
              {confirmCancel ? (
                <>
                  <button
                    type="button"
                    className="link-btn danger"
                    disabled={act.pending}
                    onClick={() =>
                      void act.run(async () => {
                        await api.cancelInterview(i.id);
                        onChanged();
                      })
                    }
                  >
                    Yes, cancel it
                  </button>
                  <button type="button" className="link-btn" onClick={() => setConfirmCancel(false)}>
                    Keep it
                  </button>
                </>
              ) : (
                <button type="button" className="link-btn danger" onClick={() => setConfirmCancel(true)}>
                  Cancel interview
                </button>
              )}
            </div>
          )}
        </>
      )}
      <ErrorNotice error={act.error} />
    </div>
  );
}

function InviteFor({ label, email, onDownload, mail, cancelled }: { label: string; email: string | null; onDownload: () => void; mail: string; cancelled: boolean }) {
  return (
    <p className="invite-for">
      <span>{label}</span>
      <button type="button" className="link-btn" onClick={onDownload}>
        {cancelled ? "Download cancellation" : "Download invite"}
      </button>
      {email ? (
        <a className="link-btn" href={mail}>
          Email {email}
        </a>
      ) : (
        <span className="hint">no email address on file</span>
      )}
    </p>
  );
}

function InterviewForm({ applicationId, editing, onCancel, onSaved }: { applicationId: string; editing: Interview | null; onCancel: () => void; onSaved: () => void }) {
  const [startDate, startTime] = editing ? utcToLondon(editing.startsAt) : ["", "10:00"];
  const [date, setDate] = useState(startDate);
  const [time, setTime] = useState(startTime);
  const [duration, setDuration] = useState(editing?.durationMinutes ?? 60);
  const [format, setFormat] = useState<Interview["format"]>(editing?.format ?? "video");
  const [location, setLocation] = useState(editing?.location ?? "");
  const [interviewers, setInterviewers] = useState(editing?.interviewers ?? "");
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const { pending, error, setError, run } = useAction();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!date || !time) return setError("Choose the date and time.");
    const input: InterviewInput = {
      startsAt: londonToUtc(date, time).toISOString(),
      durationMinutes: duration,
      format,
      location: location.trim(),
      interviewers: interviewers.trim(),
      notes: notes.trim(),
    };
    void run(async () => {
      if (editing) await api.changeInterview(editing.id, input);
      else await api.bookInterview(applicationId, input);
      onSaved();
    });
  };

  return (
    <form className="form panel" onSubmit={submit}>
      <div className="grid-3">
        <Field label="Date" type="date" required min={editing ? undefined : todayInLondon()} value={date} onChange={(e) => setDate(e.target.value)} />
        <Field label="Time (UK)" type="time" required step={300} value={time} onChange={(e) => setTime(e.target.value)} />
        <label className="field">
          <span className="label">Length</span>
          <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
            {[...new Set([...DURATIONS, duration])].sort((a, b) => a - b).map((d) => (
              <option key={d} value={d}>
                {d < 60 ? `${d} minutes` : d === 60 ? "1 hour" : `${d / 60} hours`}
              </option>
            ))}
          </select>
        </label>
      </div>
      <fieldset className="segmented">
        <legend className="label">How</legend>
        {FORMATS.map((f) => (
          <label key={f.id}>
            <input type="radio" name="format" checked={format === f.id} onChange={() => setFormat(f.id)} />
            <span>{f.label}</span>
          </label>
        ))}
      </fieldset>
      <div className="grid-2">
        <Field
          label={format === "in_person" ? "Address" : format === "video" ? "Meeting link" : "Phone number"}
          maxLength={500}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={format === "in_person" ? "e.g. 2 Wellington Place, Leeds LS1 4AP" : format === "video" ? "Teams or Zoom link" : "Who calls whom"}
        />
        <Field label="Interviewers" maxLength={500} value={interviewers} onChange={(e) => setInterviewers(e.target.value)} placeholder="e.g. Tom Bexley (CTO)" />
      </div>
      <label className="field">
        <span className="label">Note for the invites (both get it)</span>
        <textarea rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. ask for Tom at reception; bring a portfolio" />
      </label>
      <ErrorNotice error={error} />
      <div className="button-row">
        <button className="btn" disabled={pending}>
          {pending ? "Saving…" : editing ? "Save changes" : "Book interview"}
        </button>
        <button type="button" className="link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {editing && <p className="hint">After saving, send the updated invites: they replace the old ones in people's calendars.</p>}
    </form>
  );
}
